import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from "notistack";
import { setcurrentLeadID, setCurrentPageTitle } from '../../../store/slices/pageSlice';
import { clearWizardState, goNext, goToStep, setOperationProjectStatus } from '../../../store/slices/WizardSlice/wizardStepSlice';
import useMatchedProject from './useMatchedProject';
import { updateAllProjectDueDatesByQuotationId } from '../../../store/slices/projectDueDateSlice';
import { fetchAllProjects } from '../../../store/slices/projectsSlice';
import DatePicker from "react-datepicker";
import useMatchedOperationsData from './useMatchedOperationsData';
import DateConversion from '../../../utils/DateConversion';
import ProgressTracker from '../../../utils/ProgressTracker';
import CheckIcon from "@mui/icons-material/Check";
import { useCurrencySymbol } from '../../../utils/currencyUtils';
import CommaConvert from '../../../utils/CommaConvert';
import { formatAmountComma } from '../../../utils/decimalAmountUtils';
import jsPDF from "jspdf";
import { salesProjectcompleteFinal, rejectManagerProjectCompletion } from '../../../store/slices/ProjectOngoing/projectOngoingSlice';
import AdminLoader from '../../../components/common/loader/AdminLoader';
import { fetchWorkEntries } from '../../../store/slices/workentriesSlice';
import { fetchHardwareDescriptions } from '../../../store/slices/hardwareDescriptionSlice';
import { fetchDescriptionTasks } from '../../../store/slices/DescriptionTask';
import { useNavigate } from 'react-router-dom';
import { fetchLeads } from '../../../store/slices/leadsSlice';
import { markLeadAsReadById } from '../../../store/slices/leadsReadSlice';
import { fetchGroups } from '../../../store/slices/groupSlice';
import { calculateProjectOngoingProgress } from '../../../utils/calculateProjectOngoingProgress';

const ProjectOngoingWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const { formatDateByCountry, getDatePlaceholder, getDateFormat } = DateConversion();
  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();
  const { tasks } = useSelector((state) => state.descriptionTasks);
  const { entries } = useSelector((state) => state.workentries);
  const { leads } = useSelector((state) => state.leads);
  const { group: groups } = useSelector((state) => state.group);
  const createdProject = useSelector((state) => state.wizardstep.createdProject);
  const wizardPlanState = useSelector((state) => state.wizardstep.wizardPlanState);
  const wizardProjectState = useSelector((state) => state.wizardstep.wizardProjectState);
  const operationProjectName = createdProject[0]?.ProjectName;
  const operationProjectQuotationId = createdProject[0]?.quotationId ?? null;
  const { matchedProject, SalesStatus } = useMatchedProject(operationProjectName, operationProjectQuotationId);

  const calculatedProjectData = useMatchedOperationsData(
    matchedProject[0]?.Projects || []
  );

  useEffect(() => {
    dispatch(setCurrentPageTitle("Project Ongoing"));
    dispatch(fetchWorkEntries());
    dispatch(fetchHardwareDescriptions());
    dispatch(fetchDescriptionTasks());
    dispatch(fetchLeads());
    dispatch(fetchGroups());
  }, [dispatch]);

  const groupNameById = React.useMemo(() => {
    const map = new Map();
    (groups || []).forEach((g) => {
      const id = g?._id || g?.id;
      const name = g?.newGroupName || g?.groupname || g?.groupName || g?.name || "";
      if (id && name) map.set(String(id), name);
    });
    return map;
  }, [groups]);

  const getDisplayGroupName = React.useCallback(
    (item) => {
      const groupId = item?.groupid?._id || item?.groupid;
      if (groupId && groupNameById.has(String(groupId))) {
        return groupNameById.get(String(groupId));
      }
      return item?.AssignedGroup || "";
    },
    [groupNameById]
  );

  const [rowName, setRowName] = useState(matchedProject[0]?.quotationId ?? matchedProject[0]?.ProjectName)
  const [showListState, setShowListState] = useState("tasktable");
  const [currentProject, setCurrentProject] = useState({})
  const [clickedIdPerson, setClickedIdPerson] = useState(null)
  const [localDueDates, setLocalDueDates] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitProject, setSubmitProject] = useState(false)
  const [rejectMode, setRejectMode] = useState(false);
  const [selectedRejectManagers, setSelectedRejectManagers] = useState({});
  const [rejectConfirm, setRejectConfirm] = useState(false);

  const getManagerRejectKey = (projectId, empid) =>
    `${String(projectId || "")}::${String(empid || "")}`;

  const toggleManagerRejectSelection = (projectId, empid, email) => {
    const key = getManagerRejectKey(projectId, empid);
    setSelectedRejectManagers((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = { projectId, email };
      }
      return next;
    });
  };

  const exitRejectMode = () => {
    setRejectMode(false);
    setSelectedRejectManagers({});
    setRejectConfirm(false);
  };

  const handleDueDateChange = async (date, projectName, quotationId) => {
    if (!date || !projectName) return;

    // update UI immediately
    setLocalDueDates(prev => ({
      ...prev,
      [projectName]: date
    }));

    try {
      await dispatch(
        updateAllProjectDueDatesByQuotationId({
          quotationId: quotationId || projectName,
          ProjectDueDate: date
        })
      );
      await dispatch(markLeadAsReadById(createdProject[0]?._id));
    } finally {
      dispatch(fetchAllProjects());
      dispatch(fetchLeads());
      setTimeout(() => {
        setLocalDueDates(prev => {
          const copy = { ...prev };
          delete copy[projectName];
          return copy;
        });
      }, 200);
    }
  };

  const downloadPDFHardware = async (newHardwareParts) => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "Sr. No",
          "Part Name",
          "Part Number",
          "Quantity",
          `Unit Price (${currency})`,
          `Total Price (${currency})`,
          "Start Date",
          "End Date",
        ],
      ],
      body: newHardwareParts?.map((LeadReport, index) => [
        index + 1,
        LeadReport.assemblepartdescription,
        LeadReport.partno,
        LeadReport.quantity,
        USAComma(LeadReport.unitprice),
        USAComma(LeadReport.totalprice),
        LeadReport.startdate
          ? formatDateByCountry(LeadReport.startdate)
          : "",
        LeadReport.enddate
          ? formatDateByCountry(LeadReport.enddate)
          : "",
      ]),
      styles: {
        fontSize: 7,
      },
      headStyles: {
        fontSize: 8,
      },
    });
    doc.save("Hardware PartList.pdf");
    await dispatch(markLeadAsReadById(createdProject[0]?._id));
  };

  const downloadPDFHardwareDescriptions = async (newDescriptions) => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [["Sr. No", "Description", `Amount (${currency})`, `Expenses (${currency})`, "Start Date", "End Date", "Notes"]],
      body: newDescriptions?.map((LeadReport, index) => [
        index + 1,
        LeadReport.description,
        formatAmountComma(LeadReport.value),
        formatAmountComma(LeadReport.expenses),
        LeadReport.startdate
          ? formatDateByCountry(LeadReport.startdate)
          : "",
        LeadReport.enddate
          ? formatDateByCountry(LeadReport.enddate)
          : "",
        LeadReport.notes,
      ]),
      styles: {
        fontSize: 7,
      },
      headStyles: {
        fontSize: 8,
      },
    });
    doc.save("Hardware OtherExpensesList.pdf");
    await dispatch(markLeadAsReadById(createdProject[0]?._id));
    dispatch(fetchLeads());
  };

  const salesProjectcomplete = async (row) => {
    if (!row) {
      enqueueSnackbar("Failed To Complete Project", { variant: "error" });
    }
    const quotationId = row.quotationId ?? row.Projects?.[0]?.quotationId ?? row.ProjectName;
    const payload = {
      quotationId,
    };
    setIsLoading(true);
    try {
      await dispatch(salesProjectcompleteFinal(payload));
      enqueueSnackbar("Project Completed Successfully", { variant: "success" });
      await dispatch(markLeadAsReadById(createdProject[0]?._id));
      dispatch(fetchAllProjects());
    } catch {
      enqueueSnackbar("Failed To Complete Project", { variant: "error" });
    } finally {
      setIsLoading(false);
      setCurrentProject(null);
      setClickedIdPerson(null);
      setRowName("")
      dispatch(fetchLeads());
    }
  };

  const salesRejectManagers = async () => {
    const managers = Object.values(selectedRejectManagers).filter(
      (m) => m?.projectId
    );
    const uniqueByProject = [];
    const seen = new Set();
    for (const m of managers) {
      const id = String(m.projectId);
      if (seen.has(id)) continue;
      seen.add(id);
      uniqueByProject.push(m);
    }
    if (!uniqueByProject.length) {
      enqueueSnackbar("Please select at least one manager to reject", {
        variant: "warning",
      });
      return;
    }
    setIsLoading(true);
    try {
      await dispatch(
        rejectManagerProjectCompletion({ managers: uniqueByProject })
      ).unwrap();
      enqueueSnackbar("Selected manager completion(s) rejected", {
        variant: "success",
      });
      exitRejectMode();
      await dispatch(markLeadAsReadById(createdProject[0]?._id));
      dispatch(fetchAllProjects());
      dispatch(fetchLeads());
    } catch (err) {
      enqueueSnackbar(
        err?.message || "Failed to reject manager completion",
        { variant: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const calculateProgress = (project) => {
    const isProjectComplete = project?.Assignedperson?.every(
      (person) => person?.isProjectComplete
    );
    return calculateProjectOngoingProgress({
      workEntries: project?.newProject?.newworkEntries,
      descriptionEntries: project?.newProject?.newDescriptions,
      isProjectComplete: !!isProjectComplete,
    });
  };

  const handleNext = () => {
    dispatch(setOperationProjectStatus(SalesStatus))
    dispatch(goNext())
  }

  const responseassignref = useRef(null);
  useEffect(() => {
    if ((rowName)) {
      // Scroll to the right end of the container
      const scrollToPosition = responseassignref.current.scrollWidth;
      responseassignref.current.scrollTo({
        left: scrollToPosition,
        behavior: 'smooth'
      });
    }
  }, [rowName]);

  return (
    <div className='pb-6'>
      {isLoading ? <AdminLoader isLoading={isLoading} /> : <> </>}
      <div ref={responseassignref} className="w-[99%]  bg-white  h-full  min-h-[20vh] max-h-[90vh] overflow-auto pb-5 ">
        <div className="">
          <div className="bg-[#E8E8E8]  sticky top-0 z-[1299]">
            <div className="flex w-full h-full bg-[#E8E8E8]  text-xs  text-center text-black">
              <div className={`font-semibold ${rowName ? "" : "w-[80%]"} flex`}>
                <div className="w-14 border flex justify-center items-center">Sr. No</div>
                <div className={` ${rowName ? "w-28" : "w-[20%]"} p-2 break-words text-wrap flex justify-start items-center align-middle `}>
                  <span className='pl-5'>Group</span>
                </div>
                <div className={`${rowName ? "w-28" : "w-[20%]"} p-2 break-words text-wrap flex justify-start items-center `}>
                  Client Name
                </div>
                <div className={` ${rowName ? "w-28" : "w-[20%]"} p-2 break-words text-wrap flex justify-start items-center align-middle `}>
                  Project Name
                </div>
                <div className={` ${rowName ? "w-28" : "w-[20%]"} p-2 break-words text-wrap flex justify-center items-center align-middle `}>
                  Date{rowName && <br />} Requested{" "}
                </div>
                <div className={` ${rowName ? "w-28" : "w-[20%]"} p-2 break-words text-wrap flex justify-center items-center align-middle `}>
                  Due Date
                </div>
              </div>
              {rowName ? (
                <div className="flex w-full font-semibold">
                  <div className="w-36 bg-[#E8E8E8] p-2 break-words text-wrap flex justify-center items-center">
                    Manager Details
                  </div>
                  <div className="w-96 flex flex-col bg-[#E8E8E8] break-words text-wrap">
                    <div className="w-full h-[50%] break-words text-wrap flex justify-center items-center" >
                      Task Details
                    </div>
                    <div className="w-full h-[50%] break-words text-wrap flex ">
                      <div
                        onClick={() => setShowListState("tasktable")}
                        className={`w-[50%] ${showListState === "tasktable" ? "bg-blue-500 text-white" : "text-black"} p-2 cursor-pointer flex justify-center items-center`}
                      >
                        Effort Expenses
                      </div>
                      <div
                        onClick={() => setShowListState("otherexpenses")}
                        className={` w-[50%] ${showListState === "otherexpenses" ? "bg-blue-500 text-white" : "text-black"} p-2 cursor-pointer flex justify-center items-center`}>
                        Other Expenses
                      </div>
                    </div>
                  </div>
                  <div className="w-36 bg-[#E8E8E8] p-2 break-words text-wrap flex justify-center items-center">
                    Tracker
                  </div>
                </div>
              ) : (
                <div className="font-semibold w-[20%] h-13 flex justify-center items-center ">
                  <div className="w-full flex justify-center items-center h-full">Input Details</div>
                </div>
              )}

            </div>
          </div>

          <div className="w-full">
            {matchedProject.map((row, index) => {
              const rowKey = row?.quotationId ?? row?.ProjectName;
              const matchedData = calculatedProjectData?.find((data) =>
                (row?.quotationId != null && data?.quotationId != null)
                  ? String(data.quotationId) === String(row.quotationId)
                  : data?.ProjectName === row?.ProjectName
              );
              const matchedManagers = matchedData?.Projects?.filter((proj) => proj?.newProject?.isProjecttracking);
              const matchedLead = leads?.find((proj) =>
                (row?.quotationId != null && proj?.quotationId != null)
                  ? String(proj.quotationId) === String(row.quotationId)
                  : proj?.ProjectName === row?.ProjectName
              );
              return (
                <div
                  className={`flex text-xs ${matchedLead?.isRead === false ? "font-bold" : " "} w-full hover:bg-gray-100 cursor-pointer group `}
                  key={index}
                >
                  <div className={`${rowName ? "" : "w-[80%]"} flex `}>
                    <div onClick={() => {
                      setRowName(rowName === rowKey ? null : rowKey)

                    }}
                      className="w-14 group-hover:bg-gray-100 text-center border-b flex justify-start align-middle items-center pt-2">
                      <span className="pl-2">{index + 1}</span>
                    </div>
                    <div onClick={() => {
                      setRowName(rowName === rowKey ? null : rowKey)
                    }}
                      className={` ${rowName ? "w-28" : "w-[20%]"} border-b group-hover:bg-gray-100 flex justify-start align-middle items-center  p-2 break-words text-wrap `}>
                      <span className='pl-5'> {getDisplayGroupName(row)}</span>
                    </div>
                    <div className={` ${rowName ? "w-28" : "w-[20%]"} border-b group-hover:bg-gray-100 p-2 flex justify-start align-middle items-center text-center break-words text-wrap `}>
                      {createdProject?.[0]?.ClientName}
                    </div>
                    <div onClick={() => {
                      setRowName(rowName === rowKey ? null : rowKey)
                    }}
                      className={` ${rowName ? "w-28" : "w-[20%]"} border-b group-hover:bg-gray-100 flex justify-start align-middle items-center text-center p-2 break-words text-wrap overflow-hidden text-ellipsis line-clamp-2 break-all leading-snug text-xs `}>
                      {row.ProjectName}
                    </div>
                    <div onClick={() => {
                      setRowName(rowName === rowKey ? null : rowKey)
                    }}
                      className={` ${rowName ? "w-28" : "w-[20%]"} border-b group-hover:bg-gray-100 flex justify-center align-middle items-center  p-2 break-words text-wrap `}>
                      {row.DateRequested &&
                        !isNaN(new Date(row.DateRequested))
                        ? formatDateByCountry(row.DateRequested)
                        : ""}
                    </div>
                    <div
                      className={` ${rowName ? "w-28" : "w-[20%]"} border-b group-hover:bg-gray-100 flex justify-center align-middle items-center p-2 break-words text-wrap `}>

                      <DatePicker
                        selected={
                          localDueDates[rowKey]
                            ? localDueDates[rowKey]
                            : row.ProjectDueDate
                              ? new Date(row.ProjectDueDate)
                              : null
                        }
                        onChange={(date) => handleDueDateChange(date, row.ProjectName, row.quotationId)}
                        dateFormat={getDateFormat()}
                        className="w-full text-center outline-none border border-gray-300 p-2 rounded-md bg-white"
                        placeholderText={getDatePlaceholder()}
                        popperClassName="z-[1500]"
                        minDate={new Date()}
                      />
                    </div>
                  </div>

                  {rowName === rowKey ? (
                    <div className={`w-full group-hover:bg-gray-100 border-b`}>
                      <>
                        {matchedManagers?.map((project, projectIndex) => (
                          <div className=" mb-1 border-b">
                            <div key={projectIndex} className="w-full flex mb-1">
                              <div className="w-full flex flex-col ">
                                {project.AssignedPersonData?.map((proj, index) => {
                                  return (
                                    <div
                                      key={index} className="w-full flex">
                                      <div className="w-44 min-w-[10rem] text-[11px] flex flex-col justify-center items-stretch break-words [word-break:normal] hyphens-none whitespace-normal text-left">
                                        <div
                                          onClick={() => {
                                            setCurrentProject(currentProject === proj ? null : proj);
                                            setClickedIdPerson(clickedIdPerson === proj.empid ? null : proj.empid);
                                            setShowListState("tasktable")
                                          }}
                                          className="w-full flex flex-col justify-center items-stretch break-words text-wrap text-left">
                                          <div
                                            className={`w-full flex rounded-md text-[12px] text-start`}
                                          >
                                            <div className={`w-full flex p-1 items-center`}>
                                              {rejectMode &&
                                                proj?.role === "project manager" &&
                                                proj.matchedPerson?.isProjectComplete && (
                                                  <input
                                                    type="checkbox"
                                                    className="mr-1 cursor-pointer accent-red-600"
                                                    checked={Boolean(
                                                      selectedRejectManagers[
                                                        getManagerRejectKey(project._id, proj.empid)
                                                      ]
                                                    )}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => {
                                                      e.stopPropagation();
                                                      toggleManagerRejectSelection(
                                                        project._id,
                                                        proj.empid,
                                                        proj.matchedPerson?.email
                                                      );
                                                    }}
                                                    title="Select manager to reject"
                                                  />
                                                )}
                                              <div className={`${clickedIdPerson === proj.empid ? "text-blue-500" : ""} w-[85%] min-w-0 rounded-md p-1 break-words whitespace-normal text-left`}>
                                                {proj?.role === "project manager" && (
                                                  <>{proj.name}</>
                                                )}
                                                <br />
                                                <span className="text-[11px] break-words [word-break:normal] whitespace-normal text-left">
                                                  {proj?.role === "project manager" ? (
                                                    <span className="inline-block w-full max-w-full shrink-0 whitespace-nowrap text-left [word-break:normal]">
                                                      ({proj?.TypeService}
                                                      {"\u00a0"}
                                                      Manager)
                                                    </span>
                                                  ) : (
                                                    <span className="block w-full min-w-0 text-left break-words [word-break:normal] whitespace-normal">
                                                      <span className="break-words [word-break:normal]">{proj?.TypeService}</span>
                                                      {" "}
                                                      <span className="whitespace-nowrap shrink-0">{`Employee ${index}`}</span>
                                                    </span>
                                                  )}

                                                </span>
                                              </div>

                                              <div className="w-[15%] flex justify-start items-center">
                                                {/* {proj.role === "project manager" && (
                                                  <>
                                                    {proj.matchedPerson.isProjectComplete ? (

                                                      <span
                                                        title="Completed"
                                                        className="text-green-500 text-xl font-bold mr-1 rounded"
                                                      >
                                                        <CheckIcon />
                                                      </span>
                                                    ) : (
                                                      <span
                                                        title="Pending"
                                                        className="text-orange-500 text-xl font-bold ml-2 rounded"
                                                      >
                                                        !
                                                      </span>
                                                    )}
                                                  </>
                                                )} */}
                                                {proj.role === "project manager" ? (
                                                  <>
                                                    {proj.matchedPerson.isProjectComplete ? (
                                                      <span
                                                        title="Completed"
                                                        className="text-green-500 text-xl font-bold mr-1 rounded"
                                                      >
                                                        <CheckIcon />
                                                      </span>
                                                    ) : (
                                                      <span
                                                        title="Pending"
                                                        className="text-orange-500 text-xl font-bold ml-2 rounded"
                                                      >
                                                        !
                                                      </span>
                                                    )}
                                                  </>
                                                ) : (
                                                  <>
                                                    {(proj.newworkEntries?.every(
                                                      (item) => item.isTaskComplete
                                                    ) &&
                                                      proj.newDescriptions?.every(
                                                        (item) => item.isTaskComplete
                                                      )) ||
                                                      (proj.newDescriptions?.every(
                                                        (item) => item.isTaskComplete
                                                      ) &&
                                                        proj.newHardwareParts?.every(
                                                          (item) => item.isTaskComplete
                                                        )) ? (
                                                      <span
                                                        title="Completed"
                                                        className="text-green-500 text-xl font-bold mr-1 rounded"
                                                      >
                                                        <CheckIcon />
                                                      </span>
                                                    ) : (
                                                      <span
                                                        title="Pending"
                                                        className="text-orange-500 text-xl font-bold ml-2 rounded"
                                                      >
                                                        !
                                                      </span>
                                                    )}
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      <div
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-96 p-2 break-words text-wrap ">

                                        {clickedIdPerson === proj.empid ? (
                                          <>
                                            {proj?.TypeService?.toLowerCase().includes("hardware")
                                              ? showListState === "tasktable" && (
                                                <>
                                                  <div
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                    }}
                                                    className="w-full rounded-lg h-full justify-center"
                                                  >
                                                    <div
                                                      className="w-full border border-gray-300 overflow-hidden">
                                                      <div className="grid grid-cols-3 bg-blue-500 text-white text-[11px]">
                                                        <div className="p-1 border-r  text-center">
                                                          Total Parts{" "}
                                                        </div>
                                                        <div className="p-1 border-r text-center">
                                                          Total Price ({currency})
                                                        </div>
                                                        <div className="p-1 text-center">
                                                          Action
                                                        </div>
                                                      </div>

                                                      <div className="grid grid-cols-3 bg-white text-black text-[11px]">
                                                        <div className="p-1 border-r border-blue-200 text-center">
                                                          {proj?.newHardwareParts?.length}
                                                        </div>
                                                        <div className="p-1 border-r border-blue-200 text-center">
                                                          {USAComma(
                                                            proj?.newHardwareParts?.reduce(
                                                              (sum, item) => {
                                                                return (
                                                                  sum + (item.totalprice || 0)
                                                                );
                                                              },
                                                              0
                                                            )
                                                          )}{" "}
                                                        </div>
                                                        <div
                                                          title="Download Hardware Part List"
                                                          className="p-1 text-center cursor-pointer hover:bg-blue-50"
                                                          onClick={() =>
                                                            downloadPDFHardware(
                                                              proj?.newHardwareParts
                                                            )
                                                          }
                                                        >
                                                          Download
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </>
                                              )
                                              : showListState === "tasktable" && (
                                                <div className="w-full flex  gap-2">
                                                  <div className="w-full max-h-[14rem] overflow-auto">
                                                    <div className="sticky top-0 bg-[#1973eb] text-white text-[11px] text-center">
                                                      <div className="flex">
                                                        <div className="w-[30%] border p-1">
                                                          Task
                                                        </div>
                                                        <div className="w-[25%] border p-1">
                                                          Hrs Assigned
                                                        </div>
                                                        <div className="w-[25%] border p-1">
                                                          Hrs Spent
                                                        </div>
                                                        <div className="w-[20%] border p-1">
                                                          Notes
                                                        </div>
                                                      </div>
                                                      <div className="flex">
                                                        <div className="w-[30%] border p-1">
                                                          Total
                                                        </div>
                                                        <div className="w-[25%] border p-1">
                                                          {proj.newworkEntries?.reduce(
                                                            (sum, item) =>
                                                              sum + item.hoursassigned,
                                                            0
                                                          ) || 0}
                                                        </div>
                                                        <div className="w-[25%] border p-1">
                                                          {proj.newworkEntries?.reduce(
                                                            (sum, item) => sum + item.hoursspent,
                                                            0
                                                          ) || 0}
                                                        </div>
                                                        <div className="w-[20%] border p-1"></div>
                                                      </div>
                                                    </div>

                                                    {proj.newworkEntries.map((entry, index) => {
                                                      const matchedOldWorkEntry = entries.find(
                                                        (old) => old._id === entry.oldtaskid
                                                      );
                                                      return (
                                                        <div
                                                          key={index}
                                                          className="flex text-center text-[11px]"
                                                        >
                                                          <div
                                                            className="w-[30%] border p-1"
                                                            title={
                                                              matchedOldWorkEntry
                                                                ? `Previous Name: ${matchedOldWorkEntry.task}`
                                                                : ""
                                                            }
                                                          >
                                                            {entry.task}
                                                          </div>
                                                          <div
                                                            className="w-[25%] border p-1"
                                                            title={
                                                              matchedOldWorkEntry
                                                                ? `Previous Hrs: ${matchedOldWorkEntry.hoursassigned}`
                                                                : ""
                                                            }
                                                          >
                                                            {entry.hoursassigned}
                                                          </div>
                                                          <div className="w-[25%] border p-1">
                                                            {entry.hoursspent || ""}
                                                          </div>
                                                          <div
                                                            className="w-[20%] border p-1"
                                                            title={
                                                              matchedOldWorkEntry
                                                                ? `Previous Notes: ${matchedOldWorkEntry.notes}`
                                                                : ""
                                                            }
                                                          >
                                                            {entry.notes}
                                                          </div>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              )}

                                            {showListState === "otherexpenses" &&
                                              (proj?.TypeService?.toLowerCase().includes("hardware") ? (
                                                // (proj.TypeService === "Hardware" ? (
                                                <div
                                                  className="w-full border border-gray-300 overflow-hidden ">
                                                  <div className="grid grid-cols-4 bg-blue-500 text-white text-[11px]">
                                                    <div className="p-1 border-r  text-center">
                                                      Total Descriptions{" "}
                                                    </div>
                                                    <div className="p-1 border-r text-center">
                                                      Amount ({currency})
                                                    </div>
                                                    <div className="p-1 border-r text-center">
                                                      Expenses ({currency})
                                                    </div>
                                                    <div className="p-1 text-center">Action</div>
                                                  </div>

                                                  <div className="grid grid-cols-4 bg-white text-black text-[11px]">
                                                    <div className="p-1 border-r border-blue-200 text-center">
                                                      {proj?.newDescriptions?.length}
                                                    </div>
                                                    <div className="p-1 border-r border-blue-200 text-center">
                                                      {formatAmountComma(
                                                        proj?.newDescriptions?.reduce(
                                                          (sum, item) => {
                                                            return sum + (item.value || 0);
                                                          },
                                                          0
                                                        )
                                                      )}{" "}
                                                    </div>
                                                    <div className="p-1 border-r border-blue-200 text-center">
                                                      {formatAmountComma(
                                                        proj?.newDescriptions?.reduce(
                                                          (sum, item) => {
                                                            return sum + (item.expenses || 0);
                                                          },
                                                          0
                                                        )
                                                      )}{" "}
                                                    </div>
                                                    <div
                                                      title="Download Description List"
                                                      className="p-1 text-center cursor-pointer hover:bg-blue-50"
                                                      onClick={() =>
                                                        downloadPDFHardwareDescriptions(
                                                          proj?.newDescriptions
                                                        )
                                                      }
                                                    >
                                                      Download
                                                    </div>
                                                  </div>
                                                </div>
                                              ) : (
                                                <div className="w-full flex  gap-2">
                                                  <div className="w-full max-h-[14rem] overflow-auto">
                                                    <div className="sticky top-0 bg-[#1973eb] text-white text-[11px] text-center">
                                                      <div className="flex">
                                                        <div className="w-[35%] border p-1 truncate">
                                                          Description
                                                        </div>
                                                        <div className="w-[20%] border p-1">
                                                          Amount ({currency})
                                                        </div>
                                                        <div className="w-[20%] border p-1">
                                                          Expenses ({currency})
                                                        </div>
                                                        <div className="w-[25%] border p-1">
                                                          Notes
                                                        </div>
                                                      </div>
                                                      <div className="flex">
                                                        <div className="w-[35%] border p-1 truncate">
                                                          Total
                                                        </div>
                                                        <div className="w-[20%] border p-1">
                                                          {formatAmountComma(
                                                            proj?.newDescriptions?.reduce(
                                                              (sum, item) => sum + item.value,
                                                              0
                                                            ) || 0
                                                          )}
                                                        </div>
                                                        <div className="w-[20%] border p-1">
                                                          {formatAmountComma(
                                                            proj?.newDescriptions?.reduce(
                                                              (sum, item) => sum + item.expenses,
                                                              0
                                                            ) || 0
                                                          )}
                                                        </div>
                                                        <div className="w-[25%] border p-1"></div>
                                                      </div>
                                                    </div>
                                                    {proj.newDescriptions.map(
                                                      (description, index) => {
                                                        const matchedOldDescription = tasks.find(
                                                          (old) => old._id === description.oldtaskid
                                                        );
                                                        return (
                                                          <div
                                                            key={index}
                                                            className="flex text-center text-[11px]"
                                                          >
                                                            <div
                                                              className="w-[35%] border p-1 max-h-14 overflow-auto"
                                                              title={
                                                                matchedOldDescription
                                                                  ? `Previous Name: ${matchedOldDescription.description}`
                                                                  : ""
                                                              }
                                                            >
                                                              {description.description}
                                                            </div>
                                                            <div
                                                              className="w-[20%] border p-1"
                                                              title={
                                                                matchedOldDescription
                                                                  ? `Previous Value: ${matchedOldDescription.value}`
                                                                  : ""
                                                              }
                                                            >
                                                              {formatAmountComma(description.value)}
                                                            </div>
                                                            <div
                                                              className="w-[20%] border p-1"

                                                            >
                                                              {formatAmountComma(description.expenses)}
                                                            </div>
                                                            <div
                                                              className="w-[25%] border p-1 max-h-14 overflow-auto"
                                                              title={
                                                                matchedOldDescription
                                                                  ? `Previous Note: ${matchedOldDescription.notes}`
                                                                  : ""
                                                              }
                                                            >
                                                              {description.notes}
                                                            </div>
                                                          </div>
                                                        );
                                                      }
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                          </>
                                        ) : (
                                          <div
                                            onClick={() => {
                                              setCurrentProject(currentProject === proj ? null : proj);
                                              setClickedIdPerson(clickedIdPerson === proj.empid ? null : proj.empid);
                                              if (showListState === "tasktable") {
                                                setShowListState("tasktable");
                                              } else if (showListState === "otherexpenses") {
                                                setShowListState("otherexpenses");
                                              } else {
                                                setShowListState("tasktable");
                                              }
                                            }}
                                            className="flex justify-center items-center h-full w-full cursor-pointer">Task Details</div>
                                        )}
                                      </div>

                                    </div>
                                  );
                                })}

                              </div>
                              <div className="w-36 flex  justify-center items-center px-3 pt-6">
                                {project?.TypeService?.toLowerCase().includes("hardware") &&
                                  // {project.TypeService === "Hardware" && 
                                  project.Assignedperson.every(person => person.isProjectComplete) ? (
                                  <ProgressTracker value={100} />
                                ) : (
                                  <ProgressTracker value={calculateProgress(project)} />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                      <div className="w-[100%] flex justify-end items-center gap-2 px-3 py-1">
                        {matchedManagers?.every((project) =>
                          project.Assignedperson?.every(
                            (person) => person?.isProjectComplete
                          )
                        ) ? (
                          <>
                            <button
                              onClick={() => {
                                if (rejectMode) {
                                  if (Object.keys(selectedRejectManagers).length > 0) {
                                    setRejectConfirm(true);
                                  } else {
                                    exitRejectMode();
                                  }
                                } else {
                                  setRejectMode(true);
                                  setSelectedRejectManagers({});
                                }
                              }}
                              className={`py-1 px-2 rounded-md text-[11px] text-white ${
                                rejectMode && Object.keys(selectedRejectManagers).length > 0
                                  ? "bg-red-600 hover:bg-red-700"
                                  : "bg-red-500 hover:bg-red-600"
                              }`}
                            >
                              {rejectMode && Object.keys(selectedRejectManagers).length > 0
                                ? `Reject Selected Manager${Object.keys(selectedRejectManagers).length > 1 ? "s" : ""}`
                                : rejectMode
                                ? "Cancel Reject"
                                : "Reject Completion"}
                            </button>
                            <button
                              onClick={() => {
                                if (!rejectMode) setSubmitProject(true);
                              }}
                              disabled={rejectMode}
                              className={`py-1 px-2 bg-blue-600 hover:bg-blue-800 text-white rounded-md text-[11px] ${
                                rejectMode ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                            >
                              Approved Completion
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="py-1 px-2 inline-block text-orange-600 border rounded-md text-start bg-gray-100 text-[11px]">
                              All Project Completion Pending from Manager
                            </div>
                            <button
                              disabled
                              className="py-1 px-2 bg-gray-400 text-white rounded-md text-[11px] opacity-70 cursor-not-allowed"
                              title="Waiting for all managers to submit project"
                            >
                              Approved Completion
                            </button>
                          </>
                        )}
                      </div>

                      {submitProject && (
                        <div className="fixed inset-0 z-[3400] flex items-center justify-center bg-[rgba(0,0,0,0.4)] bg-opacity-50">
                          <div className="bg-white rounded-lg p-4 max-w-2xl w-[80%] max-h-[90vh] overflow-auto relative">
                            <button
                              onClick={() => setSubmitProject(false)}
                              className="absolute top-2 right-2 text-red-500 text-xl font-bold mr-6"
                            >
                              Close
                            </button>
                            <h2 className="text-[16px] text-start">
                              Project Name : <span className="font-semibold">
                                {row?.ProjectName}</span>
                            </h2>

                            <p className="mt-3 text-[14px] leading-6 text-start">
                              Are you sure you want to approve this project ?
                            </p>

                            <div className="flex justify-start gap-4 mt-4">
                              <button
                                onClick={() => {
                                  salesProjectcomplete(row)
                                  setSubmitProject(false)

                                }}
                                className="py-2 px-7 bg-[#e32278] text-white rounded-lg"
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => {
                                  setSubmitProject(false);
                                }}
                                className="py-2 px-7 bg-black hover:bg-white hover:text-black border border-black text-white rounded-lg"
                              >
                                No
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {rejectConfirm && (
                        <div className="fixed inset-0 z-[3400] flex items-center justify-center bg-[rgba(0,0,0,0.4)] bg-opacity-50">
                          <div className="bg-white rounded-lg p-4 max-w-2xl w-[80%] max-h-[90vh] overflow-auto relative">
                            <button
                              onClick={() => setRejectConfirm(false)}
                              className="absolute top-2 right-2 text-red-500 text-xl font-bold mr-6"
                            >
                              Close
                            </button>
                            <h2 className="text-[16px] text-start">
                              Project Name :{" "}
                              <span className="font-semibold">{row?.ProjectName}</span>
                            </h2>
                            <p className="mt-3 text-[14px] leading-6 text-start">
                              Are you sure you want to reject completion for the selected
                              manager{Object.keys(selectedRejectManagers).length > 1 ? "s" : ""}?
                              They will need to update and submit the project again.
                            </p>
                            <div className="flex justify-start gap-4 mt-4">
                              <button
                                onClick={() => {
                                  salesRejectManagers();
                                  setRejectConfirm(false);
                                }}
                                className="py-2 px-7 bg-red-600 text-white rounded-lg"
                              >
                                Yes, Reject
                              </button>
                              <button
                                onClick={() => setRejectConfirm(false)}
                                className="py-2 px-7 bg-black hover:bg-white hover:text-black border border-black text-white rounded-lg"
                              >
                                No
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={() => {
                        setRowName(rowName === rowKey ? null : rowKey)
                      }}
                      className={`${rowName ? "w-full" : "w-[20%]"}  group-hover:bg-gray-100 cursor-pointer border-b flex justify-center items-center`}
                    >
                      {rowName ? (
                        <div className="flex">
                          <div className="w-36 p-2 flex justify-center items-center">
                          </div>
                          <div className="w-96 p-2 flex justify-center items-center">
                            Multiple
                          </div>
                          <div className="w-36 p-2 flex justify-center items-center">
                          </div>
                        </div>
                      ) : (
                        <div className="w-full  p-2 flex justify-center items-center">
                          Multiple
                        </div>
                      )}
                    </div>
                  )}


                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className='flex justify-end w-[99%]'>

        <div className="flex gap-4 pt-2">
          <button
            onClick={() => dispatch(goToStep(1))}
            // onClick={() => dispatch(goBack())}
            className=" py-2 px-7 bg-black overflow-hidden shadow-md text-white rounded-lg"
          >
            Back
          </button>
          <button
            onClick={() => {
              if (wizardPlanState === "advancedplan" && wizardProjectState === "existingProject") {
                navigate("/dashboard");
                dispatch(clearWizardState())
                dispatch(setcurrentLeadID())
              } else {
                handleNext()
              }
            }}

            className=" py-2 px-7 bg-[#e32278] overflow-hidden shadow-md text-white rounded-lg"
          >
            {wizardPlanState === "advancedplan" && wizardProjectState === "existingProject" ? "Save" : "Next"}
          </button>

        </div>
      </div>
    </div>
  )
}

export default ProjectOngoingWizard
